/**
 * Umsetzung eines {@link DrumPattern} in eine rein geometrische
 * Notenlayout-Beschreibung ({@link ScoreLayout}).
 *
 * Das Layout kennt keine SVG- oder DOM-Details – es liefert Koordinaten
 * in einem abstrakten Zeichenraum. Dadurch ist die Positionslogik
 * (Notenköpfe, Hälse, Balken, Pausen) vollständig unit-testbar.
 */

import { DrumPattern } from '../model/DrumPattern';
import {
  INSTRUMENT_ORDER,
  type DrumInstrument,
  type Hand,
  splitSticking,
} from '../model/types';
import { voiceRhythm } from './rhythm';
import {
  INSTRUMENT_NOTATION,
  NOTE_VALUE_BEAMS,
  VOICES,
  VOICE_STEM_DIRECTION,
  type NoteHeadShape,
  type NoteValue,
  type RestValue,
  type StemDirection,
  type VoiceId,
} from './types';

/** Feste Maße des Zeichenraums (abstrakte Einheiten ≈ Pixel). */
export const GEOMETRY = {
  gap: 12,
  marginTop: 72,
  marginBottom: 60,
  noteAreaLeft: 64,
  stepWidth: 26,
  rightPad: 20,
  staffLeft: 10,
  stemLength: 12 * 3.2,
  headRx: 12 * 0.62,
  headRy: 12 * 0.5,
  beamThickness: 12 * 0.5,
  beamGap: 12 * 0.34,
  stubLength: 26 * 0.55,
} as const;

export interface StaffGeometry {
  readonly width: number;
  readonly height: number;
  readonly gap: number;
  readonly staffLeft: number;
  readonly staffRight: number;
  readonly noteAreaLeft: number;
  readonly stepWidth: number;
  /** Y-Positionen der fünf Systemlinien, oben nach unten. */
  readonly lineYs: readonly number[];
  readonly topLineY: number;
  readonly bottomLineY: number;
}

export interface LayoutHead {
  readonly x: number;
  readonly y: number;
  readonly shape: NoteHeadShape;
  readonly instrument: DrumInstrument;
}

export interface LayoutNote {
  readonly step: number;
  readonly value: NoteValue;
  readonly stemDirection: StemDirection;
  readonly stemX: number;
  /** Halsansatz an den Notenköpfen. */
  readonly stemStartY: number;
  /** Halsende (Balken- bzw. Fähnchenseite). */
  readonly stemEndY: number;
  readonly heads: readonly LayoutHead[];
  /** Anzahl Fähnchen (0, wenn die Note gebalkt ist). */
  readonly flags: number;
  readonly dotted: boolean;
  readonly beamId: number | null;
  readonly ledgerYs: readonly number[];
}

export interface LayoutRest {
  readonly x: number;
  readonly y: number;
  readonly value: RestValue;
}

export interface LayoutBeam {
  readonly x1: number;
  readonly x2: number;
  readonly y: number;
  readonly thickness: number;
  readonly kind: 'primary' | 'secondary';
}

export interface ScoreLayout {
  readonly geometry: StaffGeometry;
  readonly notes: readonly LayoutNote[];
  readonly rests: readonly LayoutRest[];
  readonly beams: readonly LayoutBeam[];
  readonly tuplets: readonly {
    x1: number;
    x2: number;
    y: number;
    direction: StemDirection;
  }[];
  readonly sticking: readonly { x: number; y: number; value: Hand }[];
}

const REST_SIXTEENTHS: Readonly<Record<RestValue, number>> = {
  sixteenth: 1,
  eighth: 2,
  quarter: 4,
};

function buildGeometry(): StaffGeometry {
  const { gap, marginTop, noteAreaLeft, stepWidth, rightPad, staffLeft } =
    GEOMETRY;
  const lineYs = [0, 1, 2, 3, 4].map((k) => marginTop + k * gap);
  const width = noteAreaLeft + 16 * stepWidth + rightPad;
  const height = marginTop + 4 * gap + GEOMETRY.marginBottom;
  return {
    width,
    height,
    gap,
    staffLeft,
    staffRight: width - rightPad / 2,
    noteAreaLeft,
    stepWidth,
    lineYs,
    topLineY: lineYs[0] as number,
    bottomLineY: lineYs[4] as number,
  };
}

/** Y-Koordinate einer Systemposition (0 = unterste Linie, 8 = oberste). */
export function positionToY(staffPosition: number): number {
  return GEOMETRY.marginTop + (8 - staffPosition) * (GEOMETRY.gap / 2);
}

/** X-Koordinate der Mitte eines Rasterschritts. */
export function stepToX(step: number): number {
  return (
    GEOMETRY.noteAreaLeft + step * GEOMETRY.stepWidth + GEOMETRY.stepWidth / 2
  );
}

/** Ledger-Linien-Positionen für einen Notenkopf außerhalb des Systems. */
function ledgerYsFor(staffPosition: number): number[] {
  const ys: number[] = [];
  for (let pos = 10; pos <= staffPosition; pos += 2) {
    ys.push(positionToY(pos));
  }
  for (let pos = -2; pos >= staffPosition; pos -= 2) {
    ys.push(positionToY(pos));
  }
  return ys;
}

function headsForVoiceStep(
  pattern: DrumPattern,
  voice: VoiceId,
  step: number,
): LayoutHead[] {
  const heads: LayoutHead[] = [];
  const x = stepToX(step);
  for (const instrument of INSTRUMENT_ORDER) {
    const notation = INSTRUMENT_NOTATION[instrument];
    if (notation.voice === voice && pattern.hasHit(instrument, step)) {
      heads.push({
        x,
        y: positionToY(notation.staffPosition),
        shape: notation.head,
        instrument,
      });
    }
  }
  return heads;
}

function collectOnsets(pattern: DrumPattern, voice: VoiceId): Set<number> {
  const onsets = new Set<number>();
  for (const step of pattern.steps) {
    for (const instrument of INSTRUMENT_ORDER) {
      if (
        INSTRUMENT_NOTATION[instrument].voice === voice &&
        pattern.hasHit(instrument, step)
      ) {
        onsets.add(step);
        break;
      }
    }
  }
  return onsets;
}

function buildNote(
  pattern: DrumPattern,
  voice: VoiceId,
  step: number,
  value: NoteValue,
  beamId: number | null,
): LayoutNote {
  const direction = VOICE_STEM_DIRECTION[voice];
  const heads = headsForVoiceStep(pattern, voice, step);
  const headYs = heads.map((h) => h.y);
  const headX = heads[0]?.x ?? stepToX(step);
  const stemX =
    direction === 'up' ? headX + GEOMETRY.headRx : headX - GEOMETRY.headRx;

  const lowest = Math.max(...headYs);
  const highest = Math.min(...headYs);
  const stemStartY = direction === 'up' ? lowest : highest;
  const stemEndY =
    direction === 'up'
      ? highest - GEOMETRY.stemLength
      : lowest + GEOMETRY.stemLength;

  const ledgerYs = heads.flatMap((h) =>
    ledgerYsFor(
      // Rückrechnung der Position aus der Y-Koordinate ist unnötig:
      // Ledger-Linien werden direkt aus der Instrumentposition bestimmt.
      INSTRUMENT_NOTATION[h.instrument].staffPosition,
    ),
  );

  const flags = beamId === null ? NOTE_VALUE_BEAMS[value] : 0;

  return {
    step,
    value,
    stemDirection: direction,
    stemX,
    stemStartY,
    stemEndY,
    heads,
    flags,
    dotted: value === 'dotted-eighth',
    beamId,
    ledgerYs,
  };
}

function buildBeams(notes: LayoutNote[]): {
  beams: LayoutBeam[];
  adjustedEndY: Map<LayoutNote, number>;
} {
  const beams: LayoutBeam[] = [];
  const adjustedEndY = new Map<LayoutNote, number>();

  const groups = new Map<number, LayoutNote[]>();
  for (const note of notes) {
    if (note.beamId === null) {
      continue;
    }
    const group = groups.get(note.beamId) ?? [];
    group.push(note);
    groups.set(note.beamId, group);
  }

  for (const group of groups.values()) {
    if (group.length < 2) {
      continue;
    }
    const direction = group[0]!.stemDirection;
    const { beamThickness, beamGap, stubLength } = GEOMETRY;

    const beamY =
      direction === 'up'
        ? Math.min(...group.map((n) => n.stemEndY))
        : Math.max(...group.map((n) => n.stemEndY));

    for (const note of group) {
      adjustedEndY.set(note, beamY);
    }

    const first = group[0]!;
    const last = group[group.length - 1]!;
    beams.push({
      x1: first.stemX,
      x2: last.stemX,
      y: beamY,
      thickness: beamThickness,
      kind: 'primary',
    });

    const secondaryY =
      direction === 'up'
        ? beamY + beamThickness + beamGap
        : beamY - beamThickness - beamGap;

    const isSixteenth = group.map((n) => n.value === 'sixteenth');

    // Durchgehende Sekundärbalken zwischen benachbarten Sechzehnteln.
    for (let i = 0; i < group.length - 1; i += 1) {
      if (isSixteenth[i] && isSixteenth[i + 1]) {
        beams.push({
          x1: group[i]!.stemX,
          x2: group[i + 1]!.stemX,
          y: secondaryY,
          thickness: beamThickness,
          kind: 'secondary',
        });
      }
    }

    // Sekundär-Stummel für isolierte Sechzehntel.
    for (let i = 0; i < group.length; i += 1) {
      if (!isSixteenth[i]) {
        continue;
      }
      const leftSixteenth = i > 0 && isSixteenth[i - 1];
      const rightSixteenth = i < group.length - 1 && isSixteenth[i + 1];
      if (leftSixteenth || rightSixteenth) {
        continue;
      }
      const stemX = group[i]!.stemX;
      const toLeft = i > 0;
      beams.push({
        x1: toLeft ? stemX - stubLength : stemX,
        x2: toLeft ? stemX : stemX + stubLength,
        y: secondaryY,
        thickness: beamThickness,
        kind: 'secondary',
      });
    }
  }

  return { beams, adjustedEndY };
}

/** Erzeugt das vollständige Notenlayout eines Patterns. */
export function layoutPattern(pattern: DrumPattern): ScoreLayout {
  const baseGeometry = buildGeometry();
  const geometry = pattern.subdivisions.some(
    (count) => count === 3 || count === 6,
  )
    ? { ...baseGeometry, height: baseGeometry.height + 24 }
    : baseGeometry;
  const notes: LayoutNote[] = [];
  const rests: LayoutRest[] = [];
  const tuplets: {
    x1: number;
    x2: number;
    y: number;
    direction: StemDirection;
  }[] = [];

  for (const voice of VOICES) {
    const onsets = collectOnsets(pattern, voice);
    const events = voiceRhythm(onsets, pattern.subdivisions);
    pattern.subdivisions.forEach((subdivision, beat) => {
      if (subdivision !== 3 && subdivision !== 6) return;
      if (![...onsets].some((step) => Math.floor(step / 4) === beat)) return;
      for (let group = 0; group < subdivision / 3; group++) {
        const start = beat * 4 + (group * 12) / subdivision;
        const groupNotes = events.filter(
          (event) =>
            event.kind === 'note' &&
            event.step >= start &&
            event.step < start + 12 / subdivision,
        );
        const headYs = groupNotes.flatMap((event) =>
          headsForVoiceStep(pattern, voice, event.step).map((head) => head.y),
        );
        const y =
          voice === 'up'
            ? Math.min(positionToY(8), ...headYs) - GEOMETRY.stemLength - 16
            : Math.max(positionToY(0), ...headYs) + GEOMETRY.stemLength + 16;
        tuplets.push({
          x1: stepToX(start) - 5,
          x2: stepToX(start + 8 / subdivision) + 5,
          y,
          direction: VOICE_STEM_DIRECTION[voice],
        });
      }
    });
    const restY = positionToY(voice === 'up' ? 6 : 2);

    for (const event of events) {
      if (event.kind === 'rest') {
        const centerX =
          GEOMETRY.noteAreaLeft +
          event.step * GEOMETRY.stepWidth +
          (REST_SIXTEENTHS[event.value] *
            GEOMETRY.stepWidth *
            ([3, 6].includes(
              pattern.subdivisions[Math.floor(event.step / 4)]!,
            ) && event.value !== 'quarter'
              ? 2 / 3
              : 1)) /
            2;
        rests.push({ x: centerX, y: restY, value: event.value });
      } else {
        notes.push(
          buildNote(pattern, voice, event.step, event.value, event.beamId),
        );
      }
    }
  }

  const { beams, adjustedEndY } = buildBeams(notes);
  const finalNotes = notes.map((note) => {
    const adjusted = adjustedEndY.get(note);
    return adjusted === undefined ? note : { ...note, stemEndY: adjusted };
  });

  const sticking = pattern.steps
    .map((step) => ({
      x: stepToX(step),
      y: geometry.height - 10,
      value: splitSticking(pattern.getSticking(step)).hand,
    }))
    .filter((mark) => mark.value !== '');
  return { geometry, notes: finalNotes, rests, beams, sticking, tuplets };
}
